import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;
import { fetchAllPages } from '../../services/fetchAllPages.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface Car {
  _id: string;
  name?: string;
}
interface FineRow {
  id: string;
  car: { id: string; label: string } | null;
  fine_type: string;
  amount_minor: number;
  currency_code: string;
  status: string;
  issued_at: string;
}

const STATUSES = ['unpaid', 'paid', 'disputed', 'waived'];

function money(minor: number, code: string) {
  return `${code} ${(minor / 100).toFixed(2)}`;
}

export function FineListPage() {
  const ability = useAbility();
  const canWrite = ability.can('create', 'Fine');
  const [carId, setCarId] = useState('');
  const [status, setStatus] = useState('');

  const { data: cars } = useQuery({
    queryKey: RESOURCE_KEYS.car.list,
    // 162 (US3): every car, not the first page of 20. `/cars` paginates with a default of 20 and
    // a maximum of 100, and this control read it with no page parameter at all — so on a fleet
    // over 20 cars the car you wanted was simply not in the list, with no error and no note.
    queryFn: () => fetchAllPages<Car>('/cars', (url) => api.get(url)),
  });
  const carName = new Map((cars ?? []).map((c) => [c._id, c.name ?? c._id]));

  // 162 (#21): a pager and a search box. `resetOn` carries this screen's own filters, so
  // changing the car or the status returns to page 1 for the same reason a search does (FR-007).
  const { page, setPage, searchInput, setSearchInput, search } = useListControls({
    resetOn: [carId, status],
  });

  const query = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (carId) query.set('car_id', carId);
  if (status) query.set('status', status);
  // Only when there is something to filter by (FR-004).
  if (search) query.set('q', search);

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.fine.list, carId, status, page, search],
    queryFn: () => api.get<{ items: FineRow[]; pages: number }>(`/fines?${query.toString()}`),
    // 063 (FR-010): a refusal is not a transient fault. Without this the caller
    // reads the empty state through three retries before the truth arrives.
    retry: false,
  });

  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/fines/${id}`),
    invalidateKey: RESOURCE_KEYS.fine.list,
  });

  const columns: Column<FineRow>[] = [
    {
      key: 'car',
      header: 'Car',
      render: (f) => f.car?.label || carName.get(f.car?.id ?? '') || 'Car unavailable',
    },
    { key: 'fine_type', header: 'Type' },
    {
      key: 'amount_minor',
      header: 'Amount',
      render: (f) => money(f.amount_minor, f.currency_code),
    },
    { key: 'status', header: 'Status', render: (f) => <StatusBadge status={f.status} /> },
    {
      key: 'id',
      header: 'Actions',
      render: (f) => (
        <RowActions
          actions={[
            canWrite && {
              key: 'edit',
              label: 'Edit',
              href: `/fines/${f.id}/edit`,
              icon: <Pencil className="h-4 w-4" />,
            },
            canWrite && {
              key: 'delete',
              label: 'Delete',
              variant: 'danger',
              confirmMessage: 'Delete this fine?',
              onClick: () => remove.run(f.id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Fines"
        description="Traffic, parking and toll fines tied to a car (and optionally a booking)."
        actions={
          canWrite ? (
            <Link to="/fines/new">
              <Button>+ New</Button>
            </Link>
          ) : undefined
        }
      />
      <Card className="mb-4 flex gap-3">
        <Select
          id="car_filter"
          aria-label="Car"
          data-testid="fine-car-filter"
          value={carId}
          onChange={(e) => setCarId((e.target as HTMLSelectElement).value)}
        >
          <option value="">All cars</option>
          {cars?.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name ?? c._id}
            </option>
          ))}
        </Select>
        <Select
          id="status_filter"
          aria-label="Status"
          data-testid="fine-status-filter"
          value={status}
          onChange={(e) => setStatus((e.target as HTMLSelectElement).value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      </Card>
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search fines by reference number"
        testId="fine-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        emptyMessage="No fines"
        // 063: a host is refused `/fines` outright and read "No fines" — a claim
        // about the fleet's record, made on the strength of a 403.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
